import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * HintsContext — global on/off + per-hint dismissal state.
 *
 * Storage:
 *   dm_hints_enabled       → "true" | "false" (default: "true")
 *   dm_dismissed_hints     → JSON array of hint ids the user has X'd out
 *
 * Surface area is intentionally tiny so removing the whole hints layer later
 * is a single grep-and-delete pass: every <Hint id="..."> call is independent.
 */

const HintsContext = createContext(null);

const STORAGE_ENABLED = "dm_hints_enabled";
const STORAGE_DISMISSED = "dm_dismissed_hints";

function readEnabled() {
  if (typeof window === "undefined") return true;
  const v = localStorage.getItem(STORAGE_ENABLED);
  return v === null ? true : v === "true";
}

function readDismissed() {
  if (typeof window === "undefined") return new Set();
  try {
    const v = localStorage.getItem(STORAGE_DISMISSED);
    if (!v) return new Set();
    const arr = JSON.parse(v);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

export function HintsProvider({ children }) {
  const [enabled, setEnabled] = useState(readEnabled);
  const [dismissed, setDismissed] = useState(readDismissed);

  // Cross-tab sync — flip the toggle in one tab, all open tabs follow.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_ENABLED) setEnabled(readEnabled());
      if (e.key === STORAGE_DISMISSED) setDismissed(readDismissed());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setEnabledPersist = useCallback((v) => {
    localStorage.setItem(STORAGE_ENABLED, v ? "true" : "false");
    setEnabled(!!v);
  }, []);

  const dismiss = useCallback((id) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      localStorage.setItem(STORAGE_DISMISSED, JSON.stringify(Array.from(next)));
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_DISMISSED);
    setDismissed(new Set());
  }, []);

  const value = {
    enabled,
    setEnabled: setEnabledPersist,
    isDismissed: (id) => dismissed.has(id),
    dismiss,
    reset,
  };

  return <HintsContext.Provider value={value}>{children}</HintsContext.Provider>;
}

export function useHints() {
  const ctx = useContext(HintsContext);
  // Soft-fall: if a Hint is mounted before the provider wraps the tree (rare,
  // but harmless), behave as if hints are enabled and nothing's dismissed.
  if (!ctx) {
    return {
      enabled: true,
      setEnabled: () => {},
      isDismissed: () => false,
      dismiss: () => {},
      reset: () => {},
    };
  }
  return ctx;
}
